import React, { useState, useEffect, useRef } from 'react';
import { Phone, MapPin, Mail, BookOpen, Users, Award, Clock, ChevronDown, ChevronLeft, ChevronRight, Menu, X, School } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import class10Results2025 from '../data/class10Results2025.json';
import class12Results2025 from '../data/class12Results2025.json';
import '../styles/LandingPage.css';

const SUBJECT_LABELS = {
  maths: 'Maths',
  science: 'Science',
  socialScience: 'SST',
  overall: 'Maths',
};

const SUBJECT_ORDER = ['maths', 'science', 'socialScience', 'overall'];

const getOrderedMarks = (marks) =>
  Object.entries(marks)
    .filter(([, value]) => value !== null && value !== undefined)
    .sort(
      ([leftSubject, leftValue], [rightSubject, rightValue]) =>
        rightValue - leftValue ||
        SUBJECT_ORDER.indexOf(leftSubject) - SUBJECT_ORDER.indexOf(rightSubject),
    )
    .map(([subject, value]) => ({
      subject,
      label: SUBJECT_LABELS[subject],
      value: Number.isInteger(value) ? String(value) : value.toFixed(0),
    }));

const getHighestMark = (marks) =>
  Math.max(...Object.values(marks).filter((mark) => mark !== null && mark !== undefined));

const rankedClass10Results = [...class10Results2025].sort(
  (left, right) =>
    getHighestMark(right.marks) - getHighestMark(left.marks) ||
    right.averageMarks - left.averageMarks ||
    right.totalMarks - left.totalMarks ||
    left.fullName.localeCompare(right.fullName),
);

const topperCarouselStudents = [...rankedClass10Results, ...rankedClass10Results];
const class12CarouselStudents = [...class12Results2025]
  .sort((left, right) => right.percentage - left.percentage)
  .map((student) => ({ ...student, marks: { overall: student.percentage } }));

const yearWiseResults = [
  {
    year: '2024-2025',
    image: '/images/results/result-2024-2025.jpg',
    description: 'Heartiest Congratulations - Class 12th & 10th Meritorious Students'
  },
  {
    year: '2022-2023',
    image: '/images/results/result-2022-2023.jpg',
    description: 'Outstanding Performance - Class 12th & 10th Toppers'
  },
  {
    year: '2021-2022',
    image: '/images/results/result-2021-2022-1.jpg',
    description: 'Class 10th 2021-22 Toppers - 100% Results'
  },
  {
    year: '2021-2022',
    image: '/images/results/result-2021-2022-2.jpg',
    description: 'Class 12th 2021-22 Maths Toppers - Congratulations'
  },
  {
    year: '2019-2020',
    image: '/images/results/result-2019-2020-1.jpg',
    description: '100% Result - Class X Toppers List'
  },
  {
    year: '2019-2020',
    image: '/images/results/result-2019-2020-2.jpg',
    description: '100% Result - Class XII Maths Toppers'
  },
  {
    year: '2018-2019',
    image: '/images/results/result-2018-2019.jpg',
    description: 'Class XII & X Toppers - Exceptional Results'
  },
  {
    year: '2017-2018',
    image: '/images/results/result-2017-2018.jpg',
    description: '100% Result - Class X Toppers List'
  }
];

const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedDemoClass, setSelectedDemoClass] = useState('');
  const [selectedDemoSubject, setSelectedDemoSubject] = useState('');
  const [isResultViewerOpen, setIsResultViewerOpen] = useState(false);
  const [activeResultImage, setActiveResultImage] = useState('');
  const [activeResultAlt, setActiveResultAlt] = useState('');
  const [isTopperCarouselDragging, setIsTopperCarouselDragging] = useState(false);
  const topperCarouselRef = useRef(null);
  const topperCarouselDragRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
  });
  const topperCarouselPauseRef = useRef(false);

  // Auto-scroll results carousel every 5 seconds
  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % yearWiseResults.length);
    }, 5000);

    return () => clearInterval(autoScrollInterval);
  // yearWiseResults is a module-level constant — length never changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isResultViewerOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsResultViewerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isResultViewerOpen]);

  // WhatsApp message handler
  const handleWhatsAppClick = (message) => {
    const phone = '919818899500';
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
  };

  const handleDemoFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const studentClass = selectedDemoClass || formData.get('class');
    const subject = selectedDemoSubject || formData.get('subject');
    const message = `Hi, I'm ${name}. I'd like to book a FREE demo class for Class ${studentClass} - ${subject}. Please let me know the available slots.`;
    handleWhatsAppClick(message);
  };

  const demoSubjectsByClass = {
    '8th': ['Maths', 'Science', 'Social Science'],
    '9th': ['Maths', 'Science', 'Social Science'],
    '10th': ['Maths', 'Science', 'Social Science'],
    '11th': ['Maths', 'Political Science', 'History', 'Geography'],
    '12th': ['Maths', 'Political Science', 'History', 'Geography']
  };

  const availableDemoSubjects = selectedDemoClass ? demoSubjectsByClass[selectedDemoClass] : [];

  const handleDemoClassChange = (e) => {
    setSelectedDemoClass(e.target.value);
    setSelectedDemoSubject('');
  };

  const handleDemoSubjectChange = (e) => {
    setSelectedDemoSubject(e.target.value);
  };

  const openResultImageViewer = (imageSrc, imageAlt) => {
    setActiveResultImage(imageSrc);
    setActiveResultAlt(imageAlt);
    setIsResultViewerOpen(true);
  };

  const closeResultImageViewer = () => {
    setIsResultViewerOpen(false);
  };

  const subjects = [
    {
      class: '8th Class',
      subjects: ['Mathematics', 'Science', 'Social Science']
    },
    {
      class: '9th - 10th Class',
      subjects: ['Mathematics', 'Science', 'Social Science']
    },
    {
      class: '11th - 12th Class',
      subjects: ['Mathematics', 'Political Science', 'Geography', 'History']
    }
  ];

  const faculty = [
    {
      name: 'Ms. Sarieta Saxena',
      qualification: 'MSc. Mathematics',
      experience: '30 years of Experience',
      subject: 'Mathematics Expert'
    },
    {
      name: 'Ms. Chetna Bagga',
      qualification: 'MSc. Biology',
      experience: 'Science Specialist',
      subject: 'Biology & Science'
    },
    {
      name: 'Mr. Vishal Saxena',
      qualification: 'Social Science Expert',
      experience: 'Experienced Educator',
      subject: 'Social Science'
    }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      class: 'Class 10th Student',
      text: 'Krishna\'s Academy helped me score 95% in my board exams. The teachers are very supportive and explain concepts clearly.',
      rating: 5
    },
    {
      name: 'Rajesh Kumar',
      class: 'Parent',
      text: 'My daughter\'s confidence and grades improved significantly after joining Krishna\'s Academy. Highly recommended!',
      rating: 5
    },
    {
      name: 'Amit Verma',
      class: 'Class 12th Student',
      text: 'Best coaching in Shalimar Garden! Small batch sizes ensure personalized attention to every student.',
      rating: 5
    }
  ];

  const whyChooseUs = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Small Batch Sizes',
      description: 'Limited students per batch for personalized attention and better interaction'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Experienced Faculty',
      description: '30+ years of combined teaching experience with proven track record'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Flexible Timings',
      description: 'Regular doubt sessions and flexible class schedules for students'
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Comprehensive Study Material',
      description: 'Well-structured notes, practice sheets, and regular tests'
    }
  ];

  const galleryImages = [
    '/images/gallery/gallery-1.jpeg',
    '/images/gallery/gallery-2.jpeg'
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % yearWiseResults.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + yearWiseResults.length) % yearWiseResults.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const normalizeTopperScroll = (value, loopWidth) => {
    if (!loopWidth) {
      return value;
    }

    let normalized = value % loopWidth;
    if (normalized < 0) {
      normalized += loopWidth;
    }

    return normalized;
  };

  useEffect(() => {
    const container = topperCarouselRef.current;
    if (!container) return undefined;

    let pos = 0;
    let target = 0;
    let animationFrameId;

    const getLoopWidth = () => container.scrollWidth / 2;
    const getCardStep = () => {
      const card = container.querySelector('.topper-carousel-card');
      if (!card) return 315;
      const gap = parseFloat(window.getComputedStyle(container).gap) || 20;
      return card.offsetWidth + gap;
    };

    const step = () => {
      if (topperCarouselDragRef.current.isDragging) {
        // Sync with drag position
        pos = container.scrollLeft;
        target = container.scrollLeft;
      } else if (!topperCarouselPauseRef.current) {
        const loopWidth = getLoopWidth();
        if (loopWidth > 0) {
          const diff = target - pos;
          pos += Math.abs(diff) > 0.1 ? diff * 0.07 : diff;
          // Loop: when past midpoint, jump both pos and target back
          if (pos >= loopWidth) {
            pos -= loopWidth;
            target -= loopWidth;
          }
          container.scrollLeft = pos;
        }
      }
      animationFrameId = window.requestAnimationFrame(step);
    };

    pos = container.scrollLeft;
    target = container.scrollLeft;
    animationFrameId = window.requestAnimationFrame(step);

    // Advance one card every 2000ms
    const intervalId = setInterval(() => {
      if (topperCarouselPauseRef.current || topperCarouselDragRef.current.isDragging) return;
      target = pos + getCardStep();
    }, 2000);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      clearInterval(intervalId);
    };
  }, []);

  const handleTopperCarouselPointerDown = (event) => {
    const container = topperCarouselRef.current;
    if (!container) {
      return;
    }

    topperCarouselPauseRef.current = true;
    topperCarouselDragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
    };
    setIsTopperCarouselDragging(true);
    container.setPointerCapture?.(event.pointerId);
  };

  const handleTopperCarouselPointerMove = (event) => {
    const container = topperCarouselRef.current;
    if (!container || !topperCarouselDragRef.current.isDragging) {
      return;
    }

    const loopWidth = container.scrollWidth / 2;
    const deltaX = event.clientX - topperCarouselDragRef.current.startX;
    const nextScrollLeft = topperCarouselDragRef.current.startScrollLeft - deltaX;

    container.scrollLeft = normalizeTopperScroll(nextScrollLeft, loopWidth);
  };

  const handleTopperCarouselPointerEnd = (event) => {
    const container = topperCarouselRef.current;
    if (!container || !topperCarouselDragRef.current.isDragging) {
      return;
    }

    topperCarouselDragRef.current.isDragging = false;
    topperCarouselPauseRef.current = false;
    setIsTopperCarouselDragging(false);

    if (container.hasPointerCapture?.(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  };

  const handleNavClick = (sectionId) => {
    closeMobileMenu();
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <div className="landing-page">
      {/* Header Navigation */}
      <header className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <img src="/images/logos/logo-main.jpg" alt="Krishna's Academy Logo" className="logo-img" />
            <span className="logo-text">Krishna's Academy</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="nav-links desktop-nav">
            <a href="#about" className="nav-link">About</a>
            <a href="#subjects" className="nav-link">Subjects</a>
            <a href="#faculty" className="nav-link">Faculty</a>
            <a href="#testimonials" className="nav-link">Testimonials</a>
            <a href="#contact" className="nav-link">Contact</a>
          </nav>
          
          <Button 
            className="btn-primary nav-cta desktop-nav"
            onClick={() => handleWhatsAppClick('Hi, I would like to know more about Krishna\'s Academy')}
          >
            <Phone className="w-4 h-4 mr-2" />
            Contact Us
          </Button>

          {/* Mobile Hamburger Menu */}
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
        )}

        {/* Mobile Sidebar Menu */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-header">
            <div className="nav-logo">
              <img src="/images/logos/logo-main.jpg" alt="Krishna's Academy Logo" className="logo-img" />
              <span className="logo-text">Krishna's Academy</span>
            </div>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="mobile-nav-links">
            <a href="#about" className="mobile-nav-link" onClick={() => handleNavClick('about')}>About</a>
            <a href="#subjects" className="mobile-nav-link" onClick={() => handleNavClick('subjects')}>Subjects</a>
            <a href="#faculty" className="mobile-nav-link" onClick={() => handleNavClick('faculty')}>Faculty</a>
            <a href="#testimonials" className="mobile-nav-link" onClick={() => handleNavClick('testimonials')}>Testimonials</a>
            <a href="#contact" className="mobile-nav-link" onClick={() => handleNavClick('contact')}>Contact</a>
          </nav>
          <div className="mobile-menu-footer">
            <Button 
              className="btn-primary btn-full"
              onClick={() => {
                closeMobileMenu();
                handleWhatsAppClick('Hi, I would like to know more about Krishna\'s Academy');
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-logo-container">
            <img src="/images/logos/logo-hero.png" alt="Krishna's Academy" className="hero-logo" />
          </div>
          <p className="hero-tagline">We Shape Your Future</p>
          <h1 className="hero-title">
            Excellence in Education,<br />Success in Results
          </h1>
          <p className="hero-subtitle">
            Leading CBSE coaching centre in Shalimar Garden, Ghaziabad.<br />
            Empowering students from Class 8th to 12th with quality education.
          </p>
          <div className="hero-cta-group">
            <Button 
              className="btn-primary btn-large"
              onClick={() => handleWhatsAppClick('Hi, I want to enroll my child at Krishna\'s Academy. Please share the details.')}
            >
              <Phone className="w-5 h-5 mr-2" />
              Enroll Now via WhatsApp
            </Button>
            <Button 
              className="btn-secondary btn-large"
              onClick={() => document.getElementById('demo-section').scrollIntoView({ behavior: 'smooth' })}
            >
              Book Free Demo Class
            </Button>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <ChevronDown className="scroll-icon" />
        </div>
      </section>

      {/* Year-wise Results Carousel */}
      <section className="results-carousel-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Our Star Achievers</h2>
            <p className="body-large section-subtitle">
              Celebrating excellence - Year-wise toppers who made us proud
            </p>
          </div>

          <div className="topper-carousel-showcase topper-carousel-showcase-class12">
            <div className="topper-carousel-header">
              <h3 className="heading-3">Class 12th 2025-26 toppers</h3>
            </div>

            <div className="topper-carousel-track topper-carousel-track-class12">
              {class12CarouselStudents.map((student, index) => {
                return (
                  <article key={student.id} className="topper-carousel-card class12-home-card">
                    <div className="topper-carousel-rank">
                      #{String(index + 1).padStart(2, '0')}
                    </div>

                    <div className="topper-carousel-student">
                      <div className="topper-carousel-photo-shell">
                        <img
                          src={student.passportImage}
                          alt={`${student.fullName} passport`}
                          className="topper-carousel-photo"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>

                      <div className="topper-carousel-copy">
                        <h4>{student.fullName}</h4>
                        <p className="topper-carousel-school">
                          <School className="w-4 h-4" />
                          <span>{student.schoolName}</span>
                        </p>
                      </div>
                    </div>

                    <div className="class12-home-score" aria-label={`Maths score ${student.percentage} percent`}>
                      <span>Maths</span>
                      <strong>{student.percentage}<small>%</small></strong>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="topper-carousel-cta">
              <Button className="btn-primary btn-dark-gradient" asChild>
                <Link to="/results/class-12-2025">View Full Class 12th Result</Link>
              </Button>
            </div>
          </div>

          <div className="topper-carousel-showcase">
            <div className="topper-carousel-header">
              <h3 className="heading-3">Class 10th 2025-26 toppers</h3>
            </div>

            <div
              className={`topper-carousel-track ${isTopperCarouselDragging ? 'dragging' : ''}`}
              ref={topperCarouselRef}
              onMouseEnter={() => {
                topperCarouselPauseRef.current = true;
              }}
              onMouseLeave={() => {
                topperCarouselPauseRef.current = false;
              }}
              onPointerDown={handleTopperCarouselPointerDown}
              onPointerMove={handleTopperCarouselPointerMove}
              onPointerUp={handleTopperCarouselPointerEnd}
              onPointerCancel={handleTopperCarouselPointerEnd}
            >
              {topperCarouselStudents.map((student, index) => {
                const orderedMarks = getOrderedMarks(student.marks);
                const rank = (index % rankedClass10Results.length) + 1;

                return (
                  <article
                    key={`${student.id}-${index}`}
                    className={`topper-carousel-card class10-home-card class10-home-card-rank-${rank} ${
                      orderedMarks.length === 2 ? 'two-subject-card' : ''
                    }`}
                  >
                    <div className="topper-carousel-rank">
                      #{String(rank).padStart(2, '0')}
                    </div>

                    <div className="topper-carousel-student">
                      <div className="topper-carousel-photo-shell">
                        <img
                          src={student.passportImage}
                          alt={`${student.fullName} passport`}
                          className="topper-carousel-photo"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>

                      <div className="topper-carousel-copy">
                        <h4>{student.fullName}</h4>
                        <p className="topper-carousel-school">
                          <School className="w-4 h-4" />
                          <span>{student.schoolName}</span>
                        </p>
                      </div>
                    </div>

                    <div className={`topper-carousel-marks marks-count-${orderedMarks.length}`}>
                      {orderedMarks.map((mark, markIndex) => (
                        <div
                          key={`${student.id}-${mark.subject}`}
                          className={`topper-carousel-mark topper-carousel-mark-${mark.subject} mark-index-${markIndex}`}
                        >
                          <span className="topper-carousel-mark-label">{mark.label}</span>
                          <span className="topper-carousel-mark-separator">-</span>
                          <span className="topper-carousel-mark-value">{mark.value}</span>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="topper-carousel-cta">
              <Button className="btn-primary btn-dark-gradient" asChild>
                <Link to="/results/class-10-2025">View Full 2025-26 Batch Result</Link>
              </Button>
            </div>
          </div>

          <div className="results-carousel-wrapper">
            <div className="custom-carousel">
              <button 
                className="carousel-button carousel-button-prev desktop-only"
                onClick={prevSlide}
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="carousel-slides">
                {yearWiseResults.map((result, index) => (
                  <div
                    key={index}
                    className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                    style={{ display: index === currentSlide ? 'flex' : 'none' }}
                  >
                    <div className="carousel-year-badge">
                      <Award className="w-5 h-5" />
                      <span>{result.year}</span>
                    </div>
                    <div className="carousel-image-container">
                      <img 
                        src={result.image} 
                        alt={`${result.year} Results`}
                        className="carousel-result-image"
                        onClick={() => openResultImageViewer(result.image, `${result.year} Results`)}
                      />
                    </div>
                    <div className="carousel-navigation-mobile">
                      <button 
                        className="carousel-button carousel-button-prev"
                        onClick={prevSlide}
                        aria-label="Previous slide"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      
                      <button 
                        className="carousel-button carousel-button-next"
                        onClick={nextSlide}
                        aria-label="Next slide"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </div>
                    <p className="carousel-description body-medium">
                      {result.description}
                    </p>
                  </div>
                ))}
              </div>
              
              <button 
                className="carousel-button carousel-button-next desktop-only"
                onClick={nextSlide}
                aria-label="Next slide"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            
            <div className="carousel-indicators">
              {yearWiseResults.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section id="subjects" className="subjects-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Subjects & Classes</h2>
            <p className="body-large section-subtitle">
              Comprehensive coaching for CBSE curriculum
            </p>
          </div>
          <div className="subjects-grid">
            {subjects.map((item, index) => (
              <Card key={index} className="subject-card">
                <CardContent className="subject-card-content">
                  <h3 className="heading-3">{item.class}</h3>
                  <ul className="subject-list">
                    {item.subjects.map((subject, idx) => (
                      <li key={idx} className="body-medium">{subject}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Our Journey</h2>
            <p className="body-large section-subtitle">
              Building futures through quality education since decades
            </p>
          </div>
          <div className="about-content">
            <div className="about-text">
              <p className="body-medium">
                <strong>Krishna's Academy</strong> has been a beacon of quality education in Shalimar Garden, Ghaziabad. 
                We are dedicated to providing comprehensive and high-quality tuition to students from various top 
                CBSE schools across the region.
              </p>
              <p className="body-medium">
                Our mission is simple: to empower every student with the knowledge, skills, and confidence they need 
                to excel in their academic journey. With experienced faculty, personalized attention, and a proven 
                track record, we have helped hundreds of students achieve their dreams.
              </p>
              <p className="body-medium">
                Located in the heart of Shalimar Garden Extension - 2, we have become the trusted choice for parents 
                and students seeking excellence in education.
              </p>
            </div>
            <div className="about-image">
              <img src="/images/about/students-learning.jpg" alt="Students Learning" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Why Choose Krishna's Academy?</h2>
            <p className="body-large section-subtitle">
              What makes us the preferred choice for students and parents
            </p>
          </div>
          <div className="why-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="why-card">
                <div className="why-icon">{item.icon}</div>
                <h3 className="heading-3">{item.title}</h3>
                <p className="body-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Faculty Section */}
      <section id="faculty" className="faculty-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Meet Our Expert Faculty</h2>
            <p className="body-large section-subtitle">
              Learn from experienced educators dedicated to your success
            </p>
          </div>
          <div className="faculty-grid">
            {faculty.map((teacher, index) => (
              <Card key={index} className="faculty-card">
                <CardContent className="faculty-card-content">
                  <div className="faculty-avatar">
                    <BookOpen className="w-12 h-12" />
                  </div>
                  <h3 className="heading-3">{teacher.name}</h3>
                  <p className="faculty-qualification">{teacher.qualification}</p>
                  <p className="body-medium faculty-experience">{teacher.experience}</p>
                  <p className="body-small faculty-subject">{teacher.subject}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">What Our Students Say</h2>
            <p className="body-large section-subtitle">
              Success stories from our students and parents
            </p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="testimonial-stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="star">★</span>
                    ))}
                  </div>
                  <p className="body-medium testimonial-text">"{testimonial.text}"</p>
                  <div className="testimonial-author">
                    <p className="body-medium author-name">{testimonial.name}</p>
                    <p className="body-small author-class">{testimonial.class}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Our Learning Environment</h2>
            <p className="body-large section-subtitle">
              A glimpse into our classrooms and learning spaces
            </p>
          </div>
          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <div key={index} className="gallery-item">
                <img src={image} alt={`Gallery ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Demo CTA Section */}
      <section id="demo-section" className="demo-section">
        <div className="container">
          <div className="demo-content">
            <div className="demo-text">
              <h2 className="heading-2">Book Your FREE Demo Class</h2>
              <p className="body-large">
                Experience our teaching methodology firsthand. Fill in your details and we'll connect with you on WhatsApp.
              </p>
            </div>
            <Card className="demo-form-card">
              <CardContent className="demo-form-content">
                <form onSubmit={handleDemoFormSubmit} className="demo-form">
                  <div className="form-group">
                    <label htmlFor="name" className="body-medium">Student Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      className="form-input" 
                      required 
                      placeholder="Enter student's name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="class" className="body-medium">Class *</label>
                    <select
                      id="class"
                      name="class"
                      className="form-input"
                      required
                      value={selectedDemoClass}
                      onChange={handleDemoClassChange}
                    >
                      <option value="">Select Class</option>
                      <option value="8th">8th Class</option>
                      <option value="9th">9th Class</option>
                      <option value="10th">10th Class</option>
                      <option value="11th">11th Class</option>
                      <option value="12th">12th Class</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject" className="body-medium">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      className="form-input"
                      required
                      value={selectedDemoSubject}
                      onChange={handleDemoSubjectChange}
                      disabled={!selectedDemoClass}
                    >
                      <option value="">Select Subject</option>
                      {availableDemoSubjects.map((subject) => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit" className="btn-primary btn-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Book Demo on WhatsApp
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="contact" className="location-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Visit Us</h2>
            <p className="body-large section-subtitle">
              Find us in the heart of Shalimar Garden, Ghaziabad
            </p>
          </div>
          <div className="location-content">
            <div className="location-info">
              <div className="info-item">
                <MapPin className="info-icon" />
                <div>
                  <h3 className="heading-3">Address</h3>
                  <p className="body-medium">
                    A-1/C-55, Shalimar Garden Extension - 2,<br />
                    Sahibabad, Ghaziabad,<br />
                    Uttar Pradesh 201005
                  </p>
                </div>
              </div>
              <div className="info-item">
                <Phone className="info-icon" />
                <div>
                  <h3 className="heading-3">Phone</h3>
                  <p className="body-medium">
                    <a href="tel:9818899500" className="link-text">9818899500</a><br />
                    <a href="tel:9910618412" className="link-text">9910618412</a>
                  </p>
                </div>
              </div>
              <div className="info-item">
                <Mail className="info-icon" />
                <div>
                  <h3 className="heading-3">Get in Touch</h3>
                  <Button 
                    className="btn-primary"
                    onClick={() => handleWhatsAppClick('Hi, I would like to know more about Krishna\'s Academy')}
                  >
                    Message on WhatsApp
                  </Button>
                </div>
              </div>
            </div>
            <div className="location-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3500.236504903702!2d77.33651907613849!3d28.68257108180535!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfa573c8705b5%3A0xeea7c663d787e467!2sKrishna&#39;s%20Academy!5e0!3m2!1sen!2sin!4v1775151262856!5m2!1sen!2sin"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Krishna's Academy Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-col">
              <img src="/images/logos/logo-main.jpg" alt="Krishna's Academy" className="footer-logo" />
              <h3 className="heading-3">Krishna's Academy</h3>
              <p className="body-small">Excellence in Education, Success in Results</p>
            </div>
            <div className="footer-col">
              <h4 className="body-medium footer-heading">Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#about" className="link-text">About Us</a></li>
                <li><a href="#subjects" className="link-text">Subjects</a></li>
                <li><a href="#faculty" className="link-text">Faculty</a></li>
                <li><a href="#testimonials" className="link-text">Testimonials</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="body-medium footer-heading">Contact</h4>
              <ul className="footer-links">
                <li className="body-small">A-1/C-55, Shalimar Garden Extension - 2</li>
                <li className="body-small">Sahibabad, Ghaziabad, UP 201005</li>
                <li><a href="tel:9818899500" className="link-text">9818899500</a></li>
                <li><a href="tel:9910618412" className="link-text">9910618412</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="body-medium footer-heading">Classes</h4>
              <ul className="footer-links">
                <li className="body-small">8th - 10th CBSE</li>
                <li className="body-small">11th - 12th CBSE</li>
                <li className="body-small">All Core Subjects</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="body-small">© 2024 Krishna's Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Sticky WhatsApp Button */}
      {isResultViewerOpen && (
        <div className="result-viewer-overlay" onClick={closeResultImageViewer}>
          <div className="result-viewer-shell" onClick={(e) => e.stopPropagation()}>
            <div className="result-viewer-image-wrap">
              <img
                src={activeResultImage}
                alt={activeResultAlt}
                className="result-viewer-image"
              />
            </div>
          </div>
        </div>
      )}

      <button 
        className="whatsapp-sticky"
        onClick={() => handleWhatsAppClick('Hi, I would like to inquire about Krishna\'s Academy')}
        aria-label="Contact on WhatsApp"
      >
        <Phone className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LandingPage;
